import React, {useState, useEffect, useRef} from 'react';
import {connect} from "react-redux";
import {useNavigate} from "react-router-dom";

import CommonLoader from "../Loaders/CommonLoader";
import agent from "../../agent";
import useTranslation from "../../customHooks/translations";
import InfiniteScroll from "react-infinite-scroller";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import PeopleIcon from '@mui/icons-material/People';
import {DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER, DEFAULT_THEME_COLOR} from "../../constants/theme";
import {FREAK_HUB_TYPE_MY} from "../../constants/commonConstants";

const HubCard = ({ hub, type, navigate, translation }) => (
  <Box
    sx={{
      background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '14px',
      overflow: 'hidden',
      mb: 2,
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': {
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        transform: 'translateY(-2px)',
      },
    }}
  >
    {hub.imageUrl && (
      <Box
        component="img"
        src={hub.imageUrl}
        alt={hub.name}
        sx={{ width: '100%', height: 160, objectFit: 'cover' }}
      />
    )}
    {!hub.imageUrl && (
      <Box
        sx={{
          width: '100%',
          height: 100,
          background: 'linear-gradient(135deg, #0d2a1e 0%, #0d1b2a 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Avatar
          src="/logo.png"
          alt={hub.name}
          sx={{ width: 64, height: 64, borderRadius: 2, opacity: 0.6 }}
        />
      </Box>
    )}
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
        {hub.name}
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', mb: 2, lineHeight: 1.5 }}>
        {hub.description}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="contained"
          size="small"
          sx={{
            background: DEFAULT_GRADIENT,
            color: 'white',
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
            zIndex: 0,
            '&:hover': { background: DEFAULT_GRADIENT_HOVER },
          }}
          onClick={() => navigate(`/freak-hub/${hub.slug}`, { state: { type } })}
        >
          {translation.hubs.view}
        </Button>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', color: 'rgba(255,255,255,0.5)' }}
          onClick={() => navigate(`/freak-hub/${hub.slug}/users`)}
        >
          <PeopleIcon sx={{ fontSize: 18, color: DEFAULT_THEME_COLOR }} />
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
            {(hub.participants?.length ?? 0) + 1}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            {translation.hubDetails.usersCount}
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

const ListHubsComponent = (props) => {
  const translation = useTranslation();
  const navigate = useNavigate();

  const [hubs, setHubs] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const pageRef = useRef(0);
  const loadingRef = useRef(false);

  useEffect(() => {
    pageRef.current = 0;
    loadingRef.current = false;
    setHubs([]);
    setHasMore(true);
    setIsLoading(true);
    loadMoreHubs();
  }, [props.type]);

  const loadMoreHubs = async () => {
    // Synchronous re-entrancy lock: react-infinite-scroller can fire loadMore
    // again while a fetch is still in flight (state-based guards are stale
    // within the same tick), which would load the same page twice.
    if (loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    setIsLoading(true);

    try {
      const results = props.type === FREAK_HUB_TYPE_MY
        ? await agent.FreakHubs.feed(pageRef.current, props.context)
        : await agent.FreakHubs.explore(pageRef.current, props.context);

      const fetched = results.freakHubs ?? [];
      setHubs(prev => [...prev, ...fetched]);
      pageRef.current += 1;

      if (fetched.length === 0) {
        setHasMore(false);
      }
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  };

  if (isLoading === false && hubs.length === 0) {
    return (
      <Box sx={{ minHeight: 150, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Button variant="outlined" sx={{ color: DEFAULT_THEME_COLOR, borderColor: DEFAULT_THEME_COLOR, zIndex: 0, mx: 1, my: 2 }}>
          {translation.hubs.emptyList}
        </Button>
      </Box>
    );
  }

  return (
    <InfiniteScroll pageStart={0} initialLoad={false} loadMore={loadMoreHubs} hasMore={hasMore} loader={<CommonLoader key="loader"/>}>
      {hubs.map(hub => (
        <HubCard key={hub.slug} hub={hub} type={props.type} navigate={navigate} translation={translation} />
      ))}
    </InfiniteScroll>
  );
};

const mapStateToProps = state => ({
  context: state.common.context,
  currentUser: state.common.currentUser,
});

export default connect(mapStateToProps, () => ({}))(ListHubsComponent);
